import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, Upload, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { SERVICES } from "@/config";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const EMPTY = {
  name: "", phone: "", email: "", service_type: "", device: "",
  description: "", preferred_contact: "phone", gdpr_consent: false,
};

// Load the Cloudflare Turnstile script once
let turnstileScriptPromise = null;
const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve();
  if (turnstileScriptPromise) return turnstileScriptPromise;
  turnstileScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true; s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("turnstile load failed"));
    document.head.appendChild(s);
  });
  return turnstileScriptPromise;
};

export const BookingForm = () => {
  const { t, lang } = useI18n();
  const b = t.booking;
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [hp, setHp] = useState(""); // honeypot
  const startTime = useRef(Date.now());
  const widgetContainer = useRef(null);
  const widgetId = useRef(null);
  const tokenRef = useRef("");
  const resolverRef = useRef(null);
  const tsErrorRef = useRef("");

  useEffect(() => {
    startTime.current = Date.now();
    if (!TURNSTILE_SITE_KEY) return;
    let cancelled = false;
    loadTurnstile().then(() => {
      if (cancelled || !window.turnstile || !widgetContainer.current || widgetId.current !== null) return;
      widgetId.current = window.turnstile.render(widgetContainer.current, {
        sitekey: TURNSTILE_SITE_KEY,
        appearance: "interaction-only",
        callback: (token) => {
          tokenRef.current = token;
          if (resolverRef.current) { resolverRef.current(token); resolverRef.current = null; }
        },
        "error-callback": (code) => {
          tsErrorRef.current = code || "unknown";
          console.error("[Turnstile] error-callback:", code);
          tokenRef.current = "";
          if (resolverRef.current) { resolverRef.current(""); resolverRef.current = null; }
        },
        "expired-callback": () => { tokenRef.current = ""; },
      });
    }).catch(() => { /* turnstile render failed; token stays empty */ });
    return () => { cancelled = true; };
  }, []);

  const resetTurnstile = () => {
    tokenRef.current = "";
    try { if (window.turnstile && widgetId.current !== null) window.turnstile.reset(widgetId.current); } catch { /* widget already unmounted */ }
  };

  const getTurnstileToken = () => new Promise((resolve) => {
    if (!TURNSTILE_SITE_KEY || !window.turnstile || widgetId.current === null) { resolve(""); return; }
    if (tokenRef.current) { resolve(tokenRef.current); return; }
    // token is auto-minted on render; wait for the callback to populate it
    resolverRef.current = resolve;
    const started = Date.now();
    const poll = setInterval(() => {
      if (tokenRef.current) { clearInterval(poll); resolverRef.current = null; resolve(tokenRef.current); }
      else if (Date.now() - started > 12000) { clearInterval(poll); resolverRef.current = null; resolve(""); }
    }, 200);
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const readFiles = async (fileList) => {
    const arr = Array.from(fileList).slice(0, 4); // max 4 files
    const tooBig = arr.find((f) => f.size > 5 * 1024 * 1024);
    if (tooBig) {
      toast.error(b.fileTooBig);
      return;
    }
    const results = await Promise.all(
      arr.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve({ filename: file.name, content: reader.result, contentType: file.type });
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file);
          })
      )
    );
    setFiles(results.filter(Boolean));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service_type || !form.device || !form.description) {
      toast.error(b.errRequired);
      return;
    }
    if (!form.gdpr_consent) {
      toast.error(b.errGdpr);
      return;
    }
    setLoading(true);
    try {
      tsErrorRef.current = "";
      const turnstile_token = await getTurnstileToken();
      if (TURNSTILE_SITE_KEY && !turnstile_token) {
        toast.error(`Overenie (Turnstile) sa nepodarilo načítať${tsErrorRef.current ? ` — kód: ${tsErrorRef.current}` : " — prázdny token"}. Nahláste tento kód správcovi.`);
        return;
      }
      const payload = {
        ...form, email: form.email || null, attachments: files, lang,
        turnstile_token, hp, elapsed_ms: Date.now() - startTime.current,
      };
      const { data } = await axios.post(`${API}/requests`, payload);
      setSuccess(data.request_number);
      setForm(EMPTY);
      setFiles([]);
      startTime.current = Date.now();
      resetTurnstile();
      toast.success(`${b.toastSentA}${data.request_number}${b.toastSentB}`);
    } catch (err) {
      resetTurnstile();
      toast.error(err?.response?.data?.detail || b.toastFail);
    } finally {
      setLoading(false);
    }
  };

  const PREF = [["phone", b.optPhone], ["email", b.optEmail], ["whatsapp", b.optWhatsapp]];

  return (
    <section id="booking" className="py-24 bg-[#f8fafc] relative" data-testid="booking-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand mb-3">{b.eyebrow}</p>
          <h2 className="font-head text-3xl sm:text-4xl font-semibold text-navy tracking-tight">
            {b.title}
          </h2>
          <p className="mt-4 text-slate-600">
            {b.subtitle}
          </p>
        </div>

        <AnimatePresence mode="wait">
          {success ? (
            <motion.div
              key="success"
              data-testid="booking-success"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-green-200 rounded-md p-10 text-center shadow-lg"
            >
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-9 h-9 text-green-600" />
              </div>
              <h3 className="font-head text-2xl font-semibold text-navy mt-5">{b.successTitle}</h3>
              <p className="text-slate-600 mt-2">{b.successNumberLabel}</p>
              <p data-testid="success-request-number" className="font-head text-4xl font-bold text-brand mt-2 tracking-wide">{success}</p>
              <p className="text-slate-500 text-sm mt-4 max-w-md mx-auto">
                {b.successNote}
              </p>
              <button
                data-testid="booking-new-request"
                onClick={() => setSuccess(null)}
                className="btn-lift mt-6 bg-navy hover:bg-navy-deep text-white font-semibold px-6 py-3 rounded-md"
              >
                {b.newRequest}
              </button>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              data-testid="booking-form"
              onSubmit={submit}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white border border-navy/10 rounded-md p-6 sm:p-10 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-5"
            >
              <div>
                <Label htmlFor="bf-name" className="text-navy font-medium">{b.name} *</Label>
                <Input id="bf-name" data-testid="input-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={b.namePh} className="mt-1.5 focus-visible:ring-brand" />
              </div>
              <div>
                <Label htmlFor="bf-phone" className="text-navy font-medium">{b.phone} *</Label>
                <Input id="bf-phone" data-testid="input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder={b.phonePh} className="mt-1.5 focus-visible:ring-brand" />
              </div>
              <div>
                <Label htmlFor="bf-email" className="text-navy font-medium">{b.email}</Label>
                <Input id="bf-email" data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder={b.emailPh} className="mt-1.5 focus-visible:ring-brand" />
              </div>
              <div>
                <Label htmlFor="bf-service-type" className="text-navy font-medium">{b.serviceType} *</Label>
                <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
                  <SelectTrigger id="bf-service-type" data-testid="select-service-type" className="mt-1.5 focus:ring-brand">
                    <SelectValue placeholder={b.serviceTypePh} />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s, i) => (
                      <SelectItem key={s.title} value={s.title} data-testid={`service-option-${s.title}`}>
                        {t.services.items[i]?.title || s.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="bf-device" className="text-navy font-medium">{b.device} *</Label>
                <Input id="bf-device" data-testid="input-device" value={form.device} onChange={(e) => set("device", e.target.value)} placeholder={b.devicePh} className="mt-1.5 focus-visible:ring-brand" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="bf-description" className="text-navy font-medium">{b.description} *</Label>
                <Textarea id="bf-description" data-testid="input-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} placeholder={b.descriptionPh} className="mt-1.5 focus-visible:ring-brand" />
              </div>

              <div className="md:col-span-2">
                <Label className="text-navy font-medium">{b.files}</Label>
                <label data-testid="input-image" className="mt-1.5 flex items-center gap-3 border border-dashed border-navy/20 rounded-md px-4 py-3 cursor-pointer hover:border-brand transition-colors">
                  <Upload className="w-5 h-5 text-slate-400" />
                  <span className="text-sm text-slate-500">
                    {files.length ? files.map((f) => f.filename).join(", ") : b.filesPh}
                  </span>
                  <input type="file" accept="image/jpeg,image/png" multiple className="hidden" onChange={(e) => readFiles(e.target.files)} />
                </label>
              </div>

              {/* Honeypot: hidden from users, bots may fill it */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "auto", height: 0, width: 0, overflow: "hidden" }}>
                <label>Nevypĺňajte toto pole
                  <input data-testid="hp-field" tabIndex={-1} autoComplete="off" type="text" value={hp} onChange={(e) => setHp(e.target.value)} />
                </label>
              </div>
              <div ref={widgetContainer} className="md:col-span-2" />

              <div className="md:col-span-2">
                <Label className="text-navy font-medium">{b.preferred}</Label>
                <RadioGroup value={form.preferred_contact} onValueChange={(v) => set("preferred_contact", v)} className="mt-2 flex flex-wrap gap-4">
                  {PREF.map(([v, l]) => (
                    <label key={v} className="flex items-center gap-2 cursor-pointer">
                      <RadioGroupItem value={v} aria-label={l} data-testid={`contact-method-${v}`} className="text-brand border-navy/30" />
                      <span className="text-sm text-slate-700">{l}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div className="md:col-span-2 flex items-start gap-3 bg-navy/[0.02] p-4 rounded-md border border-navy/10">
                <Checkbox data-testid="checkbox-gdpr" aria-labelledby="gdpr-consent-label" checked={form.gdpr_consent} onCheckedChange={(v) => set("gdpr_consent", !!v)} className="mt-0.5 data-[state=checked]:bg-brand data-[state=checked]:border-brand" />
                <label id="gdpr-consent-label" className="text-sm text-slate-600 leading-relaxed cursor-pointer" onClick={() => set("gdpr_consent", !form.gdpr_consent)}>
                  {b.gdprPre}
                  <Link to="/ochrana-osobnych-udajov" className="text-brand underline" onClick={(e) => e.stopPropagation()}>{b.gdprLink}</Link>. *
                </label>
              </div>

              <div className="md:col-span-2">
                <button
                  type="submit"
                  data-testid="booking-submit"
                  disabled={loading}
                  className="btn-lift w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold px-8 py-3.5 rounded-md shadow-lg shadow-brand/25 disabled:opacity-60"
                >
                  {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> {b.submitting}</> : <>{b.submit} <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
